// Outsourced sample routing — dispatch record + board labels.
// Vendor master / rate card come from vendor-charge-capture API (vendorChargeCapture.js).

import { canShowVendorFlow, validMedicalServiceId } from "./vendorChargeCapture";

const DISPATCH_STORE_KEY = "labOutsourcedDispatches";
const DISPATCH_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

/** @deprecated use canShowVendorFlow — kept for existing imports */
export const isOutsourceable = (orders, patient) =>
  canShowVendorFlow(orders, patient);

/** No separate OUTSOURCED-only flag from the API yet — vendor modal always offers in-house when allowed. */
export const isOutsourceOnly = () => false;

export { validMedicalServiceId, canShowVendorFlow };

const readStore = () => {
  try {
    const raw = window.localStorage.getItem(DISPATCH_STORE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (e) {
    return {};
  }
};

const writeStore = (store) => {
  try {
    window.localStorage.setItem(DISPATCH_STORE_KEY, JSON.stringify(store));
  } catch (e) {
    /* quota / private window */
  }
};

export const readDispatchMap = () => {
  const store = readStore();
  const now = Date.now();
  let pruned = false;
  Object.keys(store).forEach((k) => {
    if (now - (store[k]?.dispatchedAt || 0) > DISPATCH_MAX_AGE_MS) {
      delete store[k];
      pruned = true;
    }
  });
  if (pruned) writeStore(store);
  return store;
};

export const readDispatch = (orderNo) => {
  const key = String(orderNo || "").trim();
  if (!key) return null;
  return readDispatchMap()[key] || null;
};

export const saveDispatch = ({
  orderNos = [],
  vendor,
  dispatchRef = "",
  chargeEventsByOrder = {},
} = {}) => {
  if (!vendor?.vendor_id || orderNos.length === 0) return null;
  const record = {
    vendorId: vendor.vendor_id,
    vendorName: vendor.vendor_name,
    vendorRate: Number(vendor.vendor_rate) || 0,
    dispatchRef: String(dispatchRef || "").trim(),
    dispatchedAt: Date.now(),
    orderNos: orderNos.map(String),
    chargeEventsByOrder,
  };
  const store = readStore();
  orderNos.forEach((no) => {
    const key = String(no || "").trim();
    if (key) store[key] = record;
  });
  writeStore(store);
  return record;
};

export const clearDispatch = (orderNos = []) => {
  const store = readStore();
  orderNos.forEach((no) => delete store[String(no || "").trim()]);
  writeStore(store);
};

export const OUTSOURCED_LABEL_BY_WIRE = {
  "Sample Sent": "Dispatched to Vendor",
  "Sample Received": "Vendor Acknowledged",
  Processing: "Vendor Acknowledged",
  Completed: "Report Received",
};

/** Board label once a vendor is chosen — includes the vendor name so staff know where it went. */
export function dispatchedStatusLabel(wireStatus, dispatch) {
  const vendor = String(dispatch?.vendorName || "").trim();
  if (!vendor) return outsourcedLabel(wireStatus);
  switch (wireStatus) {
    case "Sample Sent":
      return `Sent to ${vendor}`;
    case "Sample Received":
      return `Received at ${vendor}`;
    case "Processing":
      return `Processing at ${vendor}`;
    case "Completed":
      return `Report received from ${vendor}`;
    case "Reported":
      return `Reported by ${vendor}`;
    default:
      return outsourcedLabel(wireStatus);
  }
}

export const outsourcedLabel = (wireStatus) =>
  OUTSOURCED_LABEL_BY_WIRE[wireStatus] || wireStatus;

const WIRE_BY_OUTSOURCED_LABEL = {
  "Dispatched to Vendor": "Sample Sent",
  "Vendor Acknowledged": "Sample Received",
  "Report Received": "Completed",
};

export const wireStatusFor = (label) => WIRE_BY_OUTSOURCED_LABEL[label] || label;

export const OUTSOURCED_PHLEBOTOMY_OPTIONS = [
  "Sample Collected",
  "Dispatched to Vendor",
];
export const OUTSOURCED_LAB_OPTIONS = [
  "Vendor Acknowledged",
  "Report Received",
  "Reported",
];
